import { useState, useCallback, useRef, useEffect } from 'react'
import { Aircraft, Transaction, Event } from '../types/intellijetnet'
import { AircraftTransaction } from '../types/database'
import { AircraftService } from '../services/intellijetnet/aircraft.service'
import { AircraftTransactionsService } from '../services/aircraft-transactions.service'
import {
  consolidateTransactions,
  ConsolidatedTransaction,
  CONSOLIDATION_CONFIG
} from '../utils/transaction-consolidation'

interface UseMarketSoldDataParams {
  numericModelId: number | undefined
  transactions: Transaction[] | null
  transactionsLoading: boolean
  fullSaleEvents: Event[] | null
  fullSaleEventsLoading: boolean
}

export type SoldAircraftItem = {
  aircraft: Aircraft
  transaction: ConsolidatedTransaction | null
  aircraftTransaction?: AircraftTransaction | null
}

export function useMarketSoldData({
  numericModelId,
  transactions,
  transactionsLoading,
  fullSaleEvents,
  fullSaleEventsLoading,
}: UseMarketSoldDataParams) {
  const [recentlySoldAircraft, setRecentlySoldAircraft] = useState<SoldAircraftItem[]>([])

  // Track processed transactions to prevent infinite loops
  const processedTransactionsRef = useRef<Set<number>>(new Set())
  const processedEventsRef = useRef<Set<string>>(new Set())
  const isProcessingRef = useRef(false)
  const lastTransactionHashRef = useRef<string>('')

  // Create consolidated snapshots for transactions, handling back-to-back sales
  const createConsolidatedSnapshots = useCallback(async (txns: Transaction[]) => {
    try {
      const consolidatedTransactions = consolidateTransactions(txns)
      const allSourceTransactionIds = consolidatedTransactions.flatMap(ct => ct.source_transaction_ids)
      const existingSnapshots = await AircraftTransactionsService.hasSnapshotsForApiTransactions(allSourceTransactionIds)

      const newConsolidatedTransactions = consolidatedTransactions.filter(ct => {
        return !ct.source_transaction_ids.some(id => existingSnapshots.has(id))
      })

      if (newConsolidatedTransactions.length === 0) return

      const batchSize = 5
      const batches = []
      for (let i = 0; i < newConsolidatedTransactions.length; i += batchSize) {
        batches.push(newConsolidatedTransactions.slice(i, i + batchSize))
      }

      for (const batch of batches) {
        try {
          await Promise.all(batch.map(async (consolidatedTransaction) => {
            try {
              await AircraftTransactionsService.createConsolidatedSnapshot(consolidatedTransaction)
              consolidatedTransaction.source_transaction_ids.forEach(id => {
                processedTransactionsRef.current.add(id)
              })
            } catch (error) {
              // Error creating consolidated snapshot - continue with next
            }
          }))

          if (batches.indexOf(batch) < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500))
          }
        } catch (error) {
          // Error processing batch - continue with next
        }
      }
    } catch (error) {
      // Error in consolidated snapshot creation
    }
  }, [])

  const fetchRecentlySoldAircraft = useCallback(async (txns: Transaction[]) => {
    if (isProcessingRef.current) return

    const transactionHash = txns.map(t => t.trans_id).sort().join(',')
    if (lastTransactionHashRef.current === transactionHash) return

    isProcessingRef.current = true
    lastTransactionHashRef.current = transactionHash

    try {
      // Step 1: Fetch ALL aircraft_transactions snapshots - primary data source
      const aircraftTransactions = await AircraftTransactionsService.getRecentFullSales(1440, numericModelId)

      // Step 2: Process aircraft_transactions to get aircraft data
      const snapshotPromises = aircraftTransactions.map(async (aircraftTransaction: AircraftTransaction) => {
        try {
          const aircraft = await AircraftService.getById(aircraftTransaction.aircraft_id)
          if (aircraft) {
            return { aircraft, transaction: null, aircraftTransaction }
          }
          return null
        } catch (error) {
          return null
        }
      })

      const snapshotResults = await Promise.all(snapshotPromises)
      const validSnapshotResults = snapshotResults.filter(
        (result: any): result is { aircraft: Aircraft; transaction: null; aircraftTransaction: AircraftTransaction } => result !== null
      )

      // Step 2b: Deduplicate snapshots
      const deduplicatedSnapshots = deduplicateSnapshots(validSnapshotResults)

      // Step 3: Create a set of aircraft IDs that have aircraft_transactions records
      const snapshotAircraftIds = new Set(deduplicatedSnapshots.map(result => result.aircraft.aircraft_id))

      // Step 4: For aircraft without aircraft_transactions, fall back to API transactions
      const consolidatedTransactions = consolidateTransactions(txns)
      const apiPromises = consolidatedTransactions
        .filter((ct) => !snapshotAircraftIds.has(ct.aircraft_id))
        .map(async (consolidatedTransaction) => {
          try {
            const aircraft = await AircraftService.getById(consolidatedTransaction.aircraft_id)
            if (aircraft && consolidatedTransaction) {
              return { aircraft, transaction: consolidatedTransaction, aircraftTransaction: null }
            }
            return null
          } catch (error) {
            return null
          }
        })

      const apiResults = await Promise.all(apiPromises)
      const validApiResults = apiResults.filter(
        (result): result is { aircraft: Aircraft; transaction: ConsolidatedTransaction; aircraftTransaction: null } => result !== null
      )

      // Step 5: Combine and sort
      const combinedResults = [...deduplicatedSnapshots, ...validApiResults]
      combinedResults.sort((a, b) => {
        const dateA = a.aircraftTransaction?.transaction_date || a.transaction?.trans_date || ''
        const dateB = b.aircraftTransaction?.transaction_date || b.transaction?.trans_date || ''
        return dateB.localeCompare(dateA)
      })


      setRecentlySoldAircraft(combinedResults)

      // Step 6: Create consolidated snapshots in background for API transactions
      if (consolidatedTransactions.length > 0) {
        const unprocessedTransactions = txns.filter(t => !processedTransactionsRef.current.has(t.trans_id))
        if (unprocessedTransactions.length > 0) {
          setTimeout(() => {
            createConsolidatedSnapshots(txns).catch(() => {
              // Background consolidated snapshot creation failed
            })
          }, 3000)
        }
      }
    } catch (err) {
      // Error fetching recently sold aircraft
    } finally {
      isProcessingRef.current = false
    }
  }, [createConsolidatedSnapshots, numericModelId])

  // Process transactions and Full Sale events to build sold data
  useEffect(() => {
    if (transactionsLoading || fullSaleEventsLoading) return

    const processAllSoldData = async () => {
      // Step 1: Create snapshots from Full Sale events for sales not captured in transactions table
      if (fullSaleEvents && fullSaleEvents.length > 0) {
        const transactionAircraftIds = new Set((transactions || []).map(t => t.aircraft_id))

        let existingAircraftIds = new Set<number>()
        try {
          const existingSnapshots = await AircraftTransactionsService.getRecentFullSales(1440, numericModelId)
          existingAircraftIds = new Set(existingSnapshots.map(s => s.aircraft_id))
        } catch (error) {
          // Continue even if snapshot check fails
        }

        for (const event of fullSaleEvents) {
          if (processedEventsRef.current.has(event.event_id)) continue
          processedEventsRef.current.add(event.event_id)

          if (!existingAircraftIds.has(event.aircraft_id) && !transactionAircraftIds.has(event.aircraft_id)) {
            try {
              const transactionDate = event.date.split('T')[0]
              await AircraftTransactionsService.create({
                aircraft_id: event.aircraft_id,
                model_id: event.model_id,
                transaction_date: transactionDate,
                transaction_notes: `Auto-created from Full Sale event (${event.event_id}): ${event.description || 'No description'}`
              })
              existingAircraftIds.add(event.aircraft_id)
              lastTransactionHashRef.current = ''
              console.log(`✅ Created snapshot from Full Sale event for aircraft ${event.aircraft_id}`)
            } catch (error) {
              console.error(`Error creating snapshot from Full Sale event for aircraft ${event.aircraft_id}:`, error)
            }
          }
        }
      }

      // Step 2: Fetch and display sold aircraft data
      const hasTransactions = transactions && transactions.length > 0
      const hasFullSaleEvents = fullSaleEvents && fullSaleEvents.length > 0

      if (hasTransactions || hasFullSaleEvents) {
        fetchRecentlySoldAircraft(transactions || [])
      } else if (transactions && transactions.length === 0 && (!fullSaleEvents || fullSaleEvents.length === 0)) {
        setRecentlySoldAircraft([])
        processedTransactionsRef.current.clear()
        lastTransactionHashRef.current = ''
      }
    }

    processAllSoldData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, transactionsLoading, fullSaleEvents, fullSaleEventsLoading])

  return {
    recentlySoldAircraft,
    createConsolidatedSnapshots,
  }
}

// Helper: Deduplicate snapshots — merge snapshots within the consolidation time window
function deduplicateSnapshots(
  validSnapshotResults: { aircraft: Aircraft; transaction: null; aircraftTransaction: AircraftTransaction }[]
) {
  const byAircraft = new Map<number, typeof validSnapshotResults>()
  validSnapshotResults.forEach(result => {
    const id = result.aircraft.aircraft_id
    if (!byAircraft.has(id)) byAircraft.set(id, [])
    byAircraft.get(id)!.push(result)
  })

  const deduplicated: typeof validSnapshotResults = []

  byAircraft.forEach(entries => {
    if (entries.length === 1) {
      deduplicated.push(entries[0])
      return
    }

    // Sort by transaction date (newest first)
    entries.sort((a, b) => {
      const dateA = a.aircraftTransaction.transaction_date || ''
      const dateB = b.aircraftTransaction.transaction_date || ''
      return dateB.localeCompare(dateA)
    })

    // Group by time window (mirrors consolidateTransactions logic)
    const timeGroups: (typeof validSnapshotResults)[] = []

    entries.forEach(entry => {
      const entryDate = new Date(entry.aircraftTransaction.transaction_date)
      let foundGroup = false

      for (const group of timeGroups) {
        const groupDate = new Date(group[0].aircraftTransaction.transaction_date)
        const daysDiff = Math.abs(
          (entryDate.getTime() - groupDate.getTime()) / (1000 * 60 * 60 * 24)
        )
        if (daysDiff <= CONSOLIDATION_CONFIG.TIME_WINDOW_DAYS) {
          group.push(entry)
          foundGroup = true
          break
        }
      }

      if (!foundGroup) {
        timeGroups.push([entry])
      }
    })

    // From each time group, keep only the newest entry (already sorted newest-first)
    timeGroups.forEach(group => {
      deduplicated.push(group[0])
    })
  })

  return deduplicated
}
